import { useLazyQuery } from '@apollo/client/react';
import React, { createContext, useMemo, useCallback, useRef, useEffect } from 'react';
import { NULL_USER } from '../../lib/user';
import { useUser } from '../../hooks/useUser';
import { logger } from '../../lib/logger';
import {
  GetFavoritesQuery,
  GetFavoritesQueryVariables,
  GET_FAVORITES_QUERY
} from './favorites.rest';

// creating a React context for favorites to fix performance problems caused by using a useQuery in every VariantCell
export const FavoritesContext = createContext({
  user: NULL_USER,
  favorites: [] as string[],
  getFavorites: () => {
    return;
  },
  loaded: false
});

export const FavoritesProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();

  const requestedUser = useRef<{ userId: string; accessToken: string | null } | null>(null);
  const [fetchFavorites, { data, variables }] = useLazyQuery<
    GetFavoritesQuery,
    GetFavoritesQueryVariables
  >(GET_FAVORITES_QUERY);

  useEffect(() => {
    if (!user.isLoggedIn) requestedUser.current = null;
  }, [user.isLoggedIn]);

  const hasCurrentUserData = Boolean(
    user.isLoggedIn && user.id && variables?.userId === user.id && data != null
  );

  const favorites = useMemo(
    () =>
      hasCurrentUserData
        ? data?.userFavorites?.data.attributes.variants.map(({ id }) => id) || []
        : [],
    [data, hasCurrentUserData]
  );

  const getFavorites = useCallback(() => {
    if (!user.isLoggedIn || !user.id) return;
    if (
      requestedUser.current?.userId === user.id &&
      requestedUser.current.accessToken === user.accessToken
    )
      return;

    const request = { userId: user.id, accessToken: user.accessToken };
    requestedUser.current = request;
    void fetchFavorites({
      variables: { userId: user.id },
      context: {
        headers: { 'x-user-preferences-api-token': `Bearer ${user.accessToken}` }
      }
    }).catch(error => {
      if (requestedUser.current === request) requestedUser.current = null;
      // Apollo cancels lazy queries when this provider unmounts. Other errors
      // still need to be handled because callers trigger this from an effect.
      if (error.name !== 'AbortError') logger.warn('FAVORITES', 'Could not load favorites', error);
    });
  }, [user.id, user.isLoggedIn, user.accessToken, fetchFavorites]);

  return (
    <FavoritesContext.Provider
      value={{ user, favorites, getFavorites, loaded: hasCurrentUserData }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};
